import { API_CONFIG, getApiUrl } from '../config/api';
import { handleSessionExpiry } from '../utils/sessionHandler';
import { getDeviceId } from '../utils/deviceId';

class ApiService {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.userAgent = window.navigator.userAgent;
    this.deviceId = getDeviceId();
  }

  // Helper method to create headers with optional x-authorization-token
  createHeaders(token = null, includeXAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
      'User-Agent': this.userAgent,
      'x-device-type': 'WEB',
      'x-device-id': this.deviceId
    };

    if (token && includeXAuth) {
      headers['x-authorization-token'] = token;
    }

    return headers;
  }

  async login(username, password) {
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.LOGIN), {
        method: 'POST',
        headers: this.createHeaders(null, false),  // No x-authorization-token for login
        body: JSON.stringify({ 
          login_id: username, 
          password: password,
          device: 'WEB'
        })
      });

      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.'
        };
      }

      return await response.json();
    } catch (err) {
      console.error('Login error:', err);
      return {
        status: 'error',
        message: 'Failed to connect to server. Please try again.'
      };
    }
  }

  async verify2FA(token, pin, loginId, twofaToken) {
    try {
      const twofaArray = JSON.parse(localStorage.getItem('twofa_questions') || '[]');
      
      const formattedTwofa = twofaArray.map(item => ({
        question_id: item.question_id,
        answer: pin
      }));
      
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.VERIFY_2FA), {
        method: 'POST',
        headers: this.createHeaders(token, false),  // No x-authorization-token for 2FA
        body: JSON.stringify({ 
          login_id: loginId,
          twofa_token: twofaToken,
          type: 'PIN',
          twofa: formattedTwofa
        })
      });

      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.'
        };
      }

      return await response.json();
    } catch (err) {
      console.error('2FA error:', err);
      return {
        status: 'error',
        message: 'Failed to connect to server. Please try again.'
      };
    }
  }

  async placeOrder(orderData) {
    try {
      const token = localStorage.getItem('auth_token');
      const loginId = localStorage.getItem('login_id');

      const formattedOrderData = {
        client_id: loginId,
        device: 'WEB',
        disclosed_quantity: orderData.disclosed_quantity || 0,
        exchange: orderData.exchange || 'NSE',
        execution_type: orderData.execution_type || 'REGULAR',
        instrument_token: orderData.instrument_token,
        order_side: orderData.order_side,
        order_type: orderData.order_type || 'LIMIT',
        price: orderData.price,
        product: orderData.product || 'CNC',
        quantity: orderData.quantity,
        trigger_price: orderData.trigger_price || 0,
        user_order_id: Date.now(),
        validity: orderData.validity || 'DAY'
      };

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PLACE_ORDER), {
        method: 'POST',
        headers: this.createHeaders(token),
        body: JSON.stringify(formattedOrderData)
      });

      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.'
        };
      }

      const data = await response.json();
      const isSessionExpired = await handleSessionExpiry(data);
      if (isSessionExpired) {
        return null;
      }
      return data;
    } catch (err) {
      console.error('Place order error:', err);
      return {
        status: 'error',
        message: 'Failed to connect to server. Please try again.'
      };
    }
  }
  async  modifyOrder(orderData) {
    try {
      const token = localStorage.getItem('auth_token');
      const loginId = localStorage.getItem('login_id');

      const formattedOrderData = {
        client_id: loginId,
        device: 'WEB',
        disclosed_quantity: orderData.disclosed_quantity || 0,
        exchange: orderData.exchange || 'NSE',
        execution_type: orderData.execution_type || 'REGULAR',
        instrument_token: orderData.instrument_token,
        order_side: orderData.order_side,
        order_type: orderData.order_type || 'LIMIT',
        price: orderData.price,
        product: orderData.product || 'CNC',
        quantity: orderData.quantity,
        trigger_price: orderData.trigger_price || 0,
        user_order_id: Date.now(),
        validity: orderData.validity || 'DAY',
        exchange_order_id: orderData.exchange_order_id,
        oms_order_id:orderData.oms_order_id
      };

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PLACE_ORDER), {
        method: 'PUT',
        headers: this.createHeaders(token),
        body: JSON.stringify(formattedOrderData)
      });

      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.'
        };
      }

      const data = await response.json();
      const isSessionExpired = await handleSessionExpiry(data);
      if (isSessionExpired) {
        return null;
      }
      return data;
    } catch (err) {
      console.error('Place order error:', err);
      return {
        status: 'error',
        message: 'Failed to connect to server. Please try again.'
      };
    }
  }

  async getMarketWatch() {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.MARKET_WATCH), {
        headers: this.createHeaders(token)
      });

      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.'
        };
      }

      const data = await response.json();
      const isSessionExpired = await handleSessionExpiry(data);
      if (isSessionExpired) {
        return null;
      }
      return data;
    } catch (err) {
      console.error('MarketWatch error:', err);
      return {
        status: 'error',
        message: 'Failed to fetch market watch data.'
      };
    }
  }
  async getFunds(params = {}) {
    try {
      const token = localStorage.getItem('auth_token');
  
      // Convert params object into query string
      const queryString = new URLSearchParams(params).toString();
  
      // Append query string to the API URL
      const url = `${getApiUrl(API_CONFIG.ENDPOINTS.FUNDS)}?${queryString}`;
  
      const response = await fetch(url, {
        method: 'GET',
        headers: this.createHeaders(token),
      });
      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.',
        };
      }
  
      const data = await response.json();
      const isSessionExpired = await handleSessionExpiry(data);
      if (isSessionExpired) {
        return null;
      }
  
      return data;
    } catch (err) {
      console.error('MarketWatch error:', err);
      return {
        status: 'error',
        message: 'Failed to fetch market watch data.',
      };
    }
  }
  
  async getPins() {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.DASHBOARD_PINS), {
        headers: this.createHeaders(token)
      });

      if (!response) {
        return {
          status: 'error',
          message: 'Network error. Please check your connection.'
        };
      }

      const data = await response.json();
      const isSessionExpired = await handleSessionExpiry(data);
      if (isSessionExpired) {
        return null;
      }
      return data;
    } catch (err) {
      console.error('Dashboard pins error:', err);
      return {
        status: 'error',
        message: 'Failed to fetch dashboard pins.'
      };
    }
  }

  // Add other API methods based on Postman collection
}

export const apiService = new ApiService(); 